/**
 * CategoriesManagerPanel — spec rev131 (2026-02-05)
 * --------------------------------------------------
 * Bande compacte affichée sur la page « Types de documents » : icône
 * tag + « N catégories configurées » + bouton bleu « Gérer les
 * catégories ». Au clic, ouvre un panneau plein écran (Dialog full)
 * avec un tableau détaillé : nom, nb de types, accès (public / avatars
 * empilés), actions (renommer, restreindre l'accès, supprimer).
 *
 * Réutilise le composant RestrictionDialog existant (V/C/M/S) pour
 * l'action « Restreindre l'accès ».
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import RestrictionDialog from "./RestrictionDialog";
import DocumentTypeResponsablesPopover from "./DocumentTypeResponsablesPopover";
import StepResponsibleSelect from "./StepResponsibleSelect";
import {
  Pencil, Trash2, Check, X, Tag, Users, LockIcon as LockIcon, ShieldCheck, Globe, Plus,
  Settings, Search, AlertTriangle,
} from "lucide-react";
import UserAvatar from "@/components/ui/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DeskConfirm from "@/components/crm/DeskConfirm";

const BLUE = "#0078D4";
const ORANGE = "#F97316";

function StackedAvatars({ userIds = [], usersById = {}, max = 2, bgSurface = "#161616", size = 26, borderWidth = 2 }) {
  const shown = userIds.slice(0, max);
  const extra = userIds.length - shown.length;
  return (
    <div className="flex items-center">
      <div className="flex" style={{ marginRight: extra > 0 ? 4 : 0 }}>
        {shown.map((uid, i) => {
          const u = usersById[uid];
          if (!u) return null;
          return (
            <div
              key={uid}
              className="rounded-full flex shrink-0 relative"
              style={{ border: `${borderWidth}px solid ${bgSurface}`, marginLeft: i === 0 ? 0 : -6, zIndex: shown.length - i }}
              title={`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}
            >
              <UserAvatar user={u} size={size} showPopover={false} />
            </div>
          );
        })}
      </div>
      {extra > 0 && (
        <div
          className="rounded-full flex items-center justify-center font-bold shrink-0"
          style={{
            width: size, height: size,
            background: "#2A2A2A", color: "#EDEDED",
            fontSize: Math.max(9, Math.round(size * 0.38)),
            border: `${borderWidth}px solid ${bgSurface}`,
            marginLeft: -6,
          }}
          title={`+${extra} autres utilisateurs`}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

export default function CategoriesManagerPanel({ categories, types, users, onChanged, isFirstAdmin = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [restrictOpen, setRestrictOpen] = useState(false);
  const [restrictTarget, setRestrictTarget] = useState(null);
  // Spec rev148 (2026-02-07) — modale intégrée « Nouvelle catégorie ».
  const [newOpen, setNewOpen] = useState(false);

  const usersById = useMemo(() => {
    const m = {}; (users || []).forEach((u) => { m[u.id] = u; }); return m;
  }, [users]);

  const countTypesByCat = (catId) =>
    (types || []).filter((t) => t.category_id === catId).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories || [];
    return (categories || []).filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [categories, search]);

  const saveEdit = async (patch) => {
    // Spec iter121 pt.4 (Feb 2026) — supporte désormais name + responsible_user_ids.
    if (!editing) return;
    setSaving(true);
    try {
      const body = {};
      if (patch?.name !== undefined) body.name = patch.name.trim();
      if (patch?.responsible_user_ids !== undefined) body.responsible_user_ids = patch.responsible_user_ids;
      await api.patch(`/document-type-categories/${editing.id}`, body);
      toast.success("Catégorie mise à jour");
      setEditing(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error("Impossible de modifier", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/document-type-categories/${pendingDelete.id}`);
      toast.success(`Catégorie supprimée — ${data?.types_unassigned || 0} type(s) désassigné(s)`);
      setPendingDelete(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error("Suppression impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const createNew = () => {
    // Spec rev148 (2026-02-07) — remplace window.prompt() par une vraie
    // modale intégrée avec preview du badge en temps réel.
    setNewOpen(true);
  };

  if (!categories || categories.length === 0) return null;

  return (
    <>
      {/* --- Bande compacte : N catégories configurées + bouton --- */}
      <div
        className="rounded-md flex items-center justify-between gap-3 px-3 py-2 mb-3"
        style={{ background: "#161616", border: "1px solid #2A2A2A" }}
        data-testid="categories-manager-panel"
      >
        <div className="flex items-center gap-2">
          <div
            className="rounded-md flex items-center justify-center shrink-0"
            style={{
              width: 30, height: 30,
              background: "rgba(0,120,212,0.10)",
              border: "1px solid rgba(0,120,212,0.30)",
            }}
          >
            <Tag size={15} strokeWidth={2.2} style={{ color: BLUE }} />
          </div>
          <div>
            <div className="text-[13px] font-semibold" style={{ color: "#EDEDED" }}>
              {categories.length} catégorie{categories.length > 1 ? "s" : ""} configurée{categories.length > 1 ? "s" : ""}
            </div>
            <div className="text-[10.5px]" style={{ color: "#9CA3AF" }}>
              Organisez et restreignez l'accès aux catégories de documents
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-110"
          style={{
            background: BLUE, color: "#FFFFFF",
            border: `1px solid ${BLUE}`,
            boxShadow: "0 2px 8px rgba(0,120,212,0.30)",
          }}
          data-testid="categories-manage-open"
        >
          <Settings size={14} strokeWidth={2.3} /> Gérer les catégories
        </button>
      </div>

      {/* --- Panneau plein écran --- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
          style={{ background: "#141414", border: "1px solid #2A2A2A" }}
          data-testid="categories-manager-dialog"
        >
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <DialogTitle className="text-[16px] flex items-center gap-2">
                  <Tag size={16} style={{ color: BLUE }} /> Gestion des catégories
                </DialogTitle>
                <div className="text-[11.5px] mt-1" style={{ color: "#9CA3AF" }}>
                  Renommez, supprimez ou restreignez l'accès à chaque catégorie transversale.
                </div>
              </div>
              <button
                type="button"
                onClick={createNew}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-[12.5px] font-semibold transition-all hover:brightness-110 shrink-0"
                style={{
                  background: BLUE, color: "#FFFFFF",
                  boxShadow: "0 2px 8px rgba(0,120,212,0.30)",
                }}
                data-testid="cat-new-btn"
              >
                <Plus size={14} strokeWidth={2.6} /> Nouvelle catégorie
              </button>
            </div>
          </DialogHeader>

          {/* Recherche */}
          <div className="relative mt-2 mb-3">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "#6B7280" }} />
            <input
              type="text"
              placeholder="Rechercher une catégorie…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-9 pl-8 pr-3 rounded-md text-[12.5px] focus:outline-none focus:border-[#2B579A]"
              style={{ background: "#1A1A1A", border: "1px solid #2A2A2A", color: "#EDEDED" }}
              data-testid="cat-search-input"
            />
          </div>

          {/* Tableau */}
          <div className="flex-1 overflow-y-auto rounded-md" style={{ border: "1px solid #2A2A2A" }}>
            <table className="w-full text-[12.5px]" data-testid="cat-manager-table">
              <thead style={{ background: "#1A1A1A" }} className="sticky top-0 z-10">
                <tr style={{ color: "#9CA3AF" }} className="text-[10px] uppercase tracking-widest">
                  <th className="text-left px-3 py-2.5 font-semibold w-[35%]">Catégorie</th>
                  <th className="text-center px-3 py-2.5 font-semibold w-[15%]">Types</th>
                  <th className="text-left px-3 py-2.5 font-semibold w-[35%]">Accès</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-[15%]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-[12px] italic" style={{ color: "#6B7280" }}>
                      Aucune catégorie ne correspond à votre recherche.
                    </td>
                  </tr>
                ) : filtered.map((c) => {
                  const count = countTypesByCat(c.id);
                  const restricted = !!c.restriction_active;
                  const authorizedIds = c.authorized_user_ids || [];
                  return (
                    <tr
                      key={c.id}
                      className="transition-colors hover:bg-white/[0.02]"
                      style={{ borderTop: "1px solid #2A2A2A" }}
                      data-testid={`cat-row-${c.id}`}
                    >
                      {/* Catégorie */}
                      <td className="px-3 py-2.5">
                        <span
                          className="inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-semibold uppercase"
                          style={{
                            background: `${c.color}1A`,
                            border: `1px solid ${c.color}55`,
                            color: c.color,
                            letterSpacing: "0.05em",
                          }}
                        >
                          {c.name}
                        </span>
                      </td>
                      {/* Types */}
                      <td className="text-center px-3 py-2.5">
                        <span className="tabular-nums font-semibold" style={{ color: count > 0 ? "#EDEDED" : "#6B7280" }}>
                          {count}
                        </span>
                        <span className="text-[10px] ml-1" style={{ color: "#6B7280" }}>
                          type{count !== 1 ? "s" : ""}
                        </span>
                      </td>
                      {/* Accès — spec iter128 pt.1 (Feb 2026)
                          • Public (pas de restriction) : globe vert + « Public »
                          • Restreint (avec ou sans users) : cadenas ROUGE
                            #e07a7a + avatars empilés 20 px (bordure 1.5 px).
                            Plus de texte « Aucun … » — signal purement visuel. */}
                      <td className="px-3 py-2.5">
                        {!restricted ? (
                          <div className="flex items-center gap-1.5" data-testid={`cat-access-${c.id}`}>
                            <Globe size={13} strokeWidth={2.2} style={{ color: "#22C55E" }} />
                            <span className="text-[12px]" style={{ color: "#86EFAC" }}>Public</span>
                          </div>
                        ) : (
                          <div
                            className="inline-flex items-center gap-1.5"
                            data-testid={`cat-access-${c.id}`}
                            title={authorizedIds.length === 0
                              ? "Accès restreint · aucun utilisateur autorisé"
                              : `Accès restreint · ${authorizedIds.length} utilisateur${authorizedIds.length > 1 ? "s" : ""} autorisé${authorizedIds.length > 1 ? "s" : ""}`}
                          >
                            <LockIcon size={15} strokeWidth={2.2} style={{ color: "#e07a7a" }} />
                            {authorizedIds.length > 0 && (
                              <StackedAvatars
                                userIds={authorizedIds}
                                usersById={usersById}
                                max={5}
                                bgSurface="#141414"
                                size={20}
                                borderWidth={1.5}
                              />
                            )}
                          </div>
                        )}
                      </td>
                      {/* Actions */}
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => setEditing({ ...c })}
                            className="h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors hover:bg-white/10"
                            style={{ color: "#9CA3AF" }}
                            data-testid={`cat-edit-${c.id}`}
                            title="Modifier (nom + responsables)">
                            <Pencil size={15} strokeWidth={2.1} />
                          </button>
                          <DocumentTypeResponsablesPopover
                            mode="category"
                            category={c}
                            users={users || []}
                            onSaved={onChanged}
                            triggerAsAvatars={
                              <button type="button"
                                className="h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors hover:bg-white/10"
                                style={{ color: "#9CA3AF" }}
                                data-testid={`cat-resp-${c.id}`}
                                title="Gérer les responsables (hérité par tous les types)">
                                <Users size={15} strokeWidth={2.1} />
                              </button>
                            }
                          />
                          {isFirstAdmin && (
                          <button type="button"
                            onClick={() => { setRestrictTarget({ kind: "category", id: c.id, label: c.name }); setRestrictOpen(true); }}
                            className="h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors hover:bg-white/10"
                            style={{ color: restricted ? ORANGE : "#9CA3AF" }}
                            data-testid={`cat-restrict-${c.id}`}
                            title={restricted ? "Restriction active — cliquer pour modifier" : "Restreindre l'accès"}>
                            {restricted ? <LockIcon size={15} strokeWidth={2.1} /> : <ShieldCheck size={15} strokeWidth={2.1} />}
                          </button>
                          )}
                          <button type="button" onClick={() => setPendingDelete({ ...c, typesCount: count })}
                            className="h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors hover:bg-red-500/15"
                            style={{ color: "#F87171" }}
                            data-testid={`cat-delete-${c.id}`}
                            title="Supprimer">
                            <Trash2 size={15} strokeWidth={2.1} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogs partagés */}
      <RestrictionDialog
        open={restrictOpen}
        onOpenChange={setRestrictOpen}
        kind={restrictTarget?.kind}
        id={restrictTarget?.id}
        label={restrictTarget?.label}
        users={users || []}
        onSaved={onChanged}
      />
      {/* iter415 pt 2 — suppression de catégorie : DeskConfirm R2 (garde-fou check-confirms). */}
      <DeskConfirm open={!!pendingDelete} title={`Supprimer la catégorie « ${pendingDelete?.name || "—"} » ?`}
        message={pendingDelete?.typesCount > 0
          ? `${pendingDelete.typesCount} type${pendingDelete.typesCount > 1 ? "s sont actuellement rattachés" : " est actuellement rattaché"} à cette catégorie : ${pendingDelete.typesCount > 1 ? "ils seront désassignés" : "il sera désassigné"} (« Aucune catégorie »). Les types et leurs documents ne sont pas supprimés. Action irréversible.`
          : "Aucun type ne référence cette catégorie. Action irréversible."}
        confirmLabel={saving ? "Suppression…" : "Supprimer"} busy={saving} onClose={() => { if (!saving) setPendingDelete(null); }} onConfirm={confirmDelete} testId="cat-delete-confirm" />
      {/* --- Modale « Nouvelle catégorie » — spec rev148 (2026-02-07) --- */}
      <NewCategoryDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={() => { setNewOpen(false); onChanged && onChanged(); }}
      />

      {/* --- Modale « Modifier la catégorie » — spec iter121 pt.4 (Feb 2026)
          combine renommage + gestion des responsables (verrou Premier Admin). */}
      <CategoryEditDialog
        category={editing}
        onClose={() => setEditing(null)}
        onSave={saveEdit}
        saving={saving}
      />
    </>
  );
}


/**
 * NewCategoryDialog — spec rev148 (2026-02-07).
 * Modale intégrée remplaçant l'ancien window.prompt() natif. Affiche :
 *   • Header « Nouvelle catégorie » + bouton X.
 *   • Champ « NOM » (label UPPERCASE, gris, petit) + Input texte.
 *   • Zone « APERÇU » avec badge live rendu au style catégorie
 *     (fond #COLOR1F + bordure #COLOR55 + texte #COLOR).
 *   • La couleur est fetch une seule fois depuis
 *     GET /document-type-categories/next-color pour rester stable
 *     durant toute la saisie (idempotent : la vraie assignation
 *     backend au POST utilise le même algorithme déterministe).
 *   • Boutons Annuler (outline gris) / Créer (bleu #2B579A).
 */
function NewCategoryDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [previewColor, setPreviewColor] = useState("#0078D4");
  const [saving, setSaving] = useState(false);

  // Reset à chaque ouverture + fetch de la couleur qui sera assignée.
  useEffect(() => {
    if (!open) return;
    setName("");
    setSaving(false);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/document-type-categories/next-color");
        if (!cancelled && data?.color) setPreviewColor(data.color);
      } catch {
        // fallback silencieux — bleu par défaut
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= 40 && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post(`/document-type-categories`, { name: trimmed });
      toast.success("Catégorie créée");
      onCreated && onCreated();
    } catch (err) {
      toast.error("Impossible de créer", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  // Spec rev151 (2026-02-07) — Ctrl+Entrée valide la création même si
  // le focus n'est plus sur l'input du nom, utile pour les créations
  // en rafale (ex. l'admin colle un nom, presse Tab pour vérifier
  // l'aperçu, puis Ctrl+Entrée). Handler global tant que la modale
  // est ouverte ; on scope à `open` pour ne pas polluer le reste.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        if (canSubmit) {
          e.preventDefault();
          submit();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, canSubmit, trimmed]); // eslint-disable-line react-hooks/exhaustive-deps

  const previewLabel = trimmed || "Nom de la catégorie";
  const hasName = trimmed.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        style={{ background: "#141414", border: "1px solid #2A2A2A" }}
        data-testid="cat-new-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px] flex items-center gap-2">
            <Tag size={15} style={{ color: BLUE }} /> Nouvelle catégorie
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {/* --- Champ NOM --- */}
          <div>
            <label
              htmlFor="cat-new-name"
              className="text-[11px] uppercase tracking-widest block mb-1.5"
              style={{ color: "#9CA3AF" }}
            >
              Nom <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <Input
              id="cat-new-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 40))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Ex. Achats, Ventes, Administration…"
              data-testid="cat-new-name-input"
              className="bg-transparent"
            />
            <div className="mt-1 text-[10.5px] text-right" style={{ color: "#6B7280" }}>
              {trimmed.length}/40
            </div>
          </div>

          {/* --- Aperçu badge live --- */}
          <div>
            <div
              className="text-[11px] uppercase tracking-widest mb-1.5"
              style={{ color: "#9CA3AF" }}
            >
              Aperçu
            </div>
            <div
              className="rounded-md flex items-center justify-center py-4"
              style={{
                background: "#0F0F0F",
                border: "1px dashed #2A2A2A",
              }}
              data-testid="cat-new-preview"
            >
              <span
                className="inline-flex items-center text-[10px] font-semibold uppercase px-2.5 py-0.5 rounded-full whitespace-nowrap"
                style={{
                  background: `${previewColor}1F`,
                  border: `1px solid ${previewColor}55`,
                  color: previewColor,
                  letterSpacing: 0.3,
                  opacity: hasName ? 1 : 0.5,
                }}
              >
                {previewLabel}
              </span>
            </div>
            <div className="mt-1 text-[10.5px]" style={{ color: "#6B7280" }}>
              La couleur est assignée automatiquement.
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="cat-new-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="text-white border-0 disabled:opacity-50"
            style={{ background: "#2B579A" }}
            data-testid="cat-new-submit"
          >
            {saving ? "Création…" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * CategoryEditDialog — spec iter121 pt.4 (Feb 2026).
 * Modale d'édition d'une catégorie (remplace l'ancien renommage inline).
 * Combine :
 *   • Champ NOM (obligatoire, 40 chars max, unique)
 *   • Sélecteur multi-responsables (StepResponsibleSelect) → verrou
 *     Premier Admin appliqué côté backend (403 avec message clair).
 */
function CategoryEditDialog({ category, onClose, onSave, saving }) {
  const open = !!category;
  const [name, setName] = useState("");
  const [responsibles, setResponsibles] = useState([]);

  useEffect(() => {
    if (!open) return;
    setName(category.name || "");
    setResponsibles(Array.isArray(category.responsible_user_ids)
      ? category.responsible_user_ids.filter(Boolean) : []);
  }, [open, category]);

  const trimmed = name.trim();
  const nameChanged = trimmed !== (category?.name || "").trim();
  const respChanged = JSON.stringify([...responsibles].sort())
    !== JSON.stringify([...((category?.responsible_user_ids) || [])].sort());
  const canSubmit = trimmed.length > 0 && trimmed.length <= 40
    && (nameChanged || respChanged) && !saving;

  const submit = () => {
    if (!canSubmit) return;
    const patch = {};
    if (nameChanged) patch.name = trimmed;
    if (respChanged) patch.responsible_user_ids = responsibles;
    onSave(patch);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent
        className="max-w-lg"
        style={{ background: "#141414", border: "1px solid #2A2A2A" }}
        data-testid="cat-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px] flex items-center gap-2">
            <Pencil size={15} style={{ color: BLUE }} /> Modifier la catégorie
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {/* --- Champ NOM --- */}
          <div>
            <label
              htmlFor="cat-edit-name"
              className="text-[11px] uppercase tracking-widest block mb-1.5"
              style={{ color: "#9CA3AF" }}
            >
              Nom <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <Input
              id="cat-edit-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 40))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Nom de la catégorie"
              data-testid="cat-edit-name-input"
              className="bg-transparent"
            />
            <div className="mt-1 text-[10.5px] text-right" style={{ color: "#6B7280" }}>
              {trimmed.length}/40
            </div>
          </div>

          {/* --- Responsables (verrou Premier Admin) --- */}
          <div>
            <label
              className="text-[11px] uppercase tracking-widest block mb-1.5"
              style={{ color: "#9CA3AF" }}
            >
              Responsables de la catégorie
            </label>
            <StepResponsibleSelect
              values={responsibles}
              onChange={setResponsibles}
              testId="cat-edit-responsibles"
            />
            <div className="mt-1.5 text-[10.5px] italic" style={{ color: "#9CA3AF" }}>
              Si le Premier Admin figure dans cette liste, lui seul peut la modifier.
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="cat-edit-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="text-white border-0 disabled:opacity-50"
            style={{ background: "#2B579A" }}
            data-testid="cat-edit-submit"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
